import React, { useState, useEffect, useCallback } from 'react';
import {
  Gem, DollarSign, Calculator, RefreshCw, Save, AlertCircle,
  CheckCircle, Edit2, X, Search, TrendingUp, TrendingDown, ChevronDown, ChevronUp
} from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const authHeaders = () => window.__sessionId ? { 'X-Session-ID': window.__sessionId } : {};

const fmt = {
  inr: v => v != null && !isNaN(Number(v))
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v))
    : '—',
  date: s => {
    if (!s) return '—';
    const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  },
  pct: v => v != null && !isNaN(Number(v)) ? `${Number(v)}%` : '—',
  datetime: s => s
    ? new Date(s).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—',
};

const useIsAdmin = () => {
  const { hasPermission } = usePermissions();
  return hasPermission('manual-rate-entry', 'edit');
};

function AuditLine({ show, row }) {
  if (!show || !row?.updated_by_name) return null;
  return (
    <span className="block text-[11px] text-gray-400">
      by {row.updated_by_name} · {fmt.datetime(row.updated_at)}
    </span>
  );
}

function RateCard({ icon: Icon, iconColor, title, badge, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <Icon size={18} className={iconColor} />
        <span className="font-semibold text-gray-800">{title}</span>
        {badge && <span className="ml-auto text-xs text-gray-400 font-normal">{badge}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── Gold Rate Card ───────────────────────────────────────────────────────────
function GoldRateCard({ onSaved }) {
  const isAdmin = useIsAdmin();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [input24k, setInput24k] = useState('');
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    try {
      const [todayRes, histRes] = await Promise.all([
        fetch(`${API_BASE}/rates/gold/today`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API_BASE}/rates/gold/history?limit=7`, { credentials: 'include', headers: authHeaders() }),
      ]);
      const t = await todayRes.json();
      if (t.success && t.rate) setCurrent(t.rate);
      const h = await histRes.json();
      if (h.success) setHistory(h.rates || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const v = parseFloat(input24k);
    if (!v || v <= 0) { flash('err', 'Enter a valid 24K rate'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/rates/gold/manual`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ gold_24k: v, gold_22k: Math.round(v * 0.9167), gold_18k: Math.round(v * 0.75), gold_14k: Math.round(v * 0.5833), source: 'manual entry' }),
      });
      const data = await res.json();
      if (data.success) { flash('ok', 'Gold rate saved'); setEditing(false); setInput24k(''); await load(); onSaved?.(); }
      else flash('err', data.message || 'Save failed');
    } catch { flash('err', 'Network error'); }
    finally { setSaving(false); }
  };

  const fetchApi = async () => {
    setFetching(true);
    try {
      const res = await fetch(`${API_BASE}/rates/gold/fetch`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const data = await res.json();
      if (data.success) { flash('ok', 'Fetched from API'); await load(); onSaved?.(); }
      else flash('err', data.message || 'Fetch failed');
    } catch { flash('err', 'Network error'); }
    finally { setFetching(false); }
  };

  const prev = history[1];
  const trend = current && prev ? parseFloat(current.gold_24k) - parseFloat(prev.gold_24k) : null;

  return (
    <RateCard icon={Gem} iconColor="text-amber-500" title="Gold Rate" badge={current ? fmt.date(current.rate_date) : undefined}>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">24K per gram</p>
          <p className="text-3xl font-bold text-amber-700">{current ? fmt.inr(current.gold_24k) : '—'}</p>
        </div>
        {trend !== null && (
          <div className={`flex items-center gap-0.5 mb-1 text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
            {trend >= 0 ? '+' : ''}{Math.round(trend)}
          </div>
        )}
      </div>

      {msg && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-3 ${msg.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {msg.text}
        </div>
      )}

      {editing ? (
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">24K Rate (₹ per gram)</label>
            <input type="number" step="1" autoFocus value={input24k} onChange={e => setInput24k(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="e.g. 6980" />
            {input24k && !isNaN(parseFloat(input24k)) && (
              <p className="text-xs text-gray-400 mt-1">22K ≈ {fmt.inr(Math.round(parseFloat(input24k) * 0.9167))} · 18K ≈ {fmt.inr(Math.round(parseFloat(input24k) * 0.75))}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition">
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setInput24k(''); }} className="px-3 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition">
              <X size={14} />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 mb-4">
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition">
            <Edit2 size={12} /> Edit
          </button>
          <button onClick={fetchApi} disabled={fetching} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition">
            <RefreshCw size={12} className={fetching ? 'animate-spin' : ''} /> {fetching ? 'Fetching…' : 'Fetch API'}
          </button>
        </div>
      )}

      <button onClick={() => setShowHistory(v => !v)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition">
        {showHistory ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Recent history
      </button>
      {showHistory && (
        <div className="mt-2 space-y-1.5">
          {history.slice(0, 7).map((r, i) => (
            <div key={r.id} className="flex justify-between items-start py-1 border-b border-gray-50 last:border-0">
              <span className="text-gray-500 text-xs">{fmt.date(r.rate_date)}{i === 0 && <span className="ml-1.5 text-green-600 font-medium">Today</span>}</span>
              <span className="text-right">
                <span className="text-sm font-semibold text-gray-700">{fmt.inr(r.gold_24k)}</span>
                <AuditLine show={isAdmin} row={r} />
              </span>
            </div>
          ))}
        </div>
      )}
    </RateCard>
  );
}

// ─── Dollar Rate Card ─────────────────────────────────────────────────────────
function DollarRateCard({ onSaved }) {
  const isAdmin = useIsAdmin();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [inputRate, setInputRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    try {
      const [todayRes, histRes] = await Promise.all([
        fetch(`${API_BASE}/rates/dollar/today`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API_BASE}/rates/dollar/history?limit=7`, { credentials: 'include', headers: authHeaders() }),
      ]);
      const t = await todayRes.json();
      if (t.success && t.rate) setCurrent(t.rate);
      const h = await histRes.json();
      if (h.success) setHistory(h.rates || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const v = parseFloat(inputRate);
    if (!v || v <= 0) { flash('err', 'Enter a valid rate'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/rates/dollar/manual`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ usd_to_inr: v, source: 'manual entry' }),
      });
      const data = await res.json();
      if (data.success) { flash('ok', 'Dollar rate saved'); setEditing(false); setInputRate(''); await load(); onSaved?.(); }
      else flash('err', data.message || 'Save failed');
    } catch { flash('err', 'Network error'); }
    finally { setSaving(false); }
  };

  const fetchApi = async () => {
    setFetching(true);
    try {
      const res = await fetch(`${API_BASE}/rates/dollar/fetch`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const data = await res.json();
      if (data.success) { flash('ok', 'Fetched from API'); await load(); onSaved?.(); }
      else flash('err', data.message || 'Fetch failed');
    } catch { flash('err', 'Network error'); }
    finally { setFetching(false); }
  };

  const prev = history[1];
  const trend = current && prev ? parseFloat(current.usd_to_inr) - parseFloat(prev.usd_to_inr) : null;

  return (
    <RateCard icon={DollarSign} iconColor="text-green-600" title="Dollar Rate" badge={current ? fmt.date(current.rate_date) : undefined}>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">1 USD = INR</p>
          <p className="text-3xl font-bold text-green-700">{current ? `₹${parseFloat(current.usd_to_inr).toFixed(2)}` : '—'}</p>
        </div>
        {trend !== null && (
          <div className={`flex items-center gap-0.5 mb-1 text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
            {trend >= 0 ? '+' : ''}{Math.abs(trend).toFixed(2)}
          </div>
        )}
      </div>

      {msg && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-3 ${msg.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {msg.text}
        </div>
      )}

      {editing ? (
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">USD → INR Rate</label>
            <input type="number" step="0.01" autoFocus value={inputRate} onChange={e => setInputRate(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" placeholder="e.g. 84.20" />
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition">
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setInputRate(''); }} className="px-3 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition">
              <X size={14} />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 mb-4">
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition">
            <Edit2 size={12} /> Edit
          </button>
          <button onClick={fetchApi} disabled={fetching} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition">
            <RefreshCw size={12} className={fetching ? 'animate-spin' : ''} /> {fetching ? 'Fetching…' : 'Fetch API'}
          </button>
        </div>
      )}

      <button onClick={() => setShowHistory(v => !v)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition">
        {showHistory ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Recent history
      </button>
      {showHistory && (
        <div className="mt-2 space-y-1.5">
          {history.slice(0, 7).map((r, i) => (
            <div key={r.id} className="flex justify-between items-start py-1 border-b border-gray-50 last:border-0">
              <span className="text-gray-500 text-xs">{fmt.date(r.rate_date)}{i === 0 && <span className="ml-1.5 text-green-600 font-medium">Today</span>}</span>
              <span className="text-right">
                <span className="text-sm font-semibold text-gray-700">₹{parseFloat(r.usd_to_inr).toFixed(2)}</span>
                <AuditLine show={isAdmin} row={r} />
              </span>
            </div>
          ))}
        </div>
      )}
    </RateCard>
  );
}

// ─── Shared Date Lookup Panel ─────────────────────────────────────────────────
function DateLookupPanel() {
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [gold, setGold] = useState(null);   // rate row | 'not-found' | null
  const [dollar, setDollar] = useState(null);

  // Per-rate entry state for missing dates
  const [goldInput, setGoldInput] = useState('');
  const [goldSaving, setGoldSaving] = useState(false);
  const [dollarInput, setDollarInput] = useState('');
  const [dollarSaving, setDollarSaving] = useState(false);
  const [dollarFetching, setDollarFetching] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); };

  const lookup = async () => {
    if (!date) return;
    setLoading(true);
    setGold(null); setDollar(null);
    setGoldInput(''); setDollarInput('');
    try {
      const [gRes, dRes] = await Promise.all([
        fetch(`${API_BASE}/rates/gold/date?date=${date}`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API_BASE}/rates/dollar/date?date=${date}`, { credentials: 'include', headers: authHeaders() }),
      ]);
      const gData = await gRes.json();
      const dData = await dRes.json();
      setGold(gData.success && gData.rate ? gData.rate : 'not-found');
      setDollar(dData.success && dData.rate ? dData.rate : 'not-found');
    } catch { flash('err', 'Network error during lookup'); }
    finally { setLoading(false); }
  };

  const saveGold = async () => {
    const v = parseFloat(goldInput);
    if (!v || v <= 0) return;
    setGoldSaving(true);
    try {
      const res = await fetch(`${API_BASE}/rates/gold/manual`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ gold_24k: v, gold_22k: Math.round(v * 0.9167), gold_18k: Math.round(v * 0.75), gold_14k: Math.round(v * 0.5833), date, source: 'manual entry (backfill)' }),
      });
      const data = await res.json();
      if (data.success) { flash('ok', `Gold rate saved for ${fmt.date(date)}`); setGold(data.rate); setGoldInput(''); }
      else flash('err', data.message || 'Save failed');
    } catch { flash('err', 'Network error'); }
    finally { setGoldSaving(false); }
  };

  const saveDollar = async () => {
    const v = parseFloat(dollarInput);
    if (!v || v <= 0) return;
    setDollarSaving(true);
    try {
      const res = await fetch(`${API_BASE}/rates/dollar/manual`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ usd_to_inr: v, date, source: 'manual entry (backfill)' }),
      });
      const data = await res.json();
      if (data.success) { flash('ok', `Dollar rate saved for ${fmt.date(date)}`); setDollar(data.rate); setDollarInput(''); }
      else flash('err', data.message || 'Save failed');
    } catch { flash('err', 'Network error'); }
    finally { setDollarSaving(false); }
  };

  const fetchDollar = async () => {
    setDollarFetching(true);
    try {
      const res = await fetch(`${API_BASE}/rates/dollar/fetch-date`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ date }),
      });
      const data = await res.json();
      if (data.success) { flash('ok', `Dollar rate fetched for ${fmt.date(date)}`); setDollar(data.rate); }
      else flash('err', data.message || 'Fetch failed');
    } catch { flash('err', 'Network error'); }
    finally { setDollarFetching(false); }
  };

  const hasResult = gold !== null || dollar !== null;
  const today = new Date().toLocaleDateString('en-CA'); // browser's local date (YYYY-MM-DD), not UTC
  const isPast = date && date < today;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <Search size={18} className="text-blue-500" />
        <span className="font-semibold text-gray-800">Look Up Rates by Date</span>
        <span className="ml-auto text-xs text-gray-400">Gold + Dollar</span>
      </div>
      <div className="p-5">
        {/* Date picker row */}
        <div className="flex gap-3">
          <input
            type="date" value={date} max={today}
            onChange={e => { setDate(e.target.value); setGold(null); setDollar(null); setMsg(null); }}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button onClick={lookup} disabled={!date || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition">
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
            {loading ? 'Looking up…' : 'Look Up'}
          </button>
        </div>

        {/* Feedback */}
        {msg && (
          <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${msg.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
            {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {msg.text}
          </div>
        )}

        {/* Results */}
        {hasResult && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Gold result */}
            <div className={`rounded-xl border p-4 ${gold && gold !== 'not-found' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Gem size={15} className="text-amber-500" />
                <span className="text-sm font-semibold text-gray-700">Gold Rate</span>
                <span className="text-xs text-gray-400 ml-auto">{fmt.date(date)}</span>
              </div>

              {gold && gold !== 'not-found' ? (
                <p className="text-2xl font-bold text-amber-700">{fmt.inr(gold.gold_24k)} <span className="text-sm font-normal text-amber-600">/ gram (24K)</span></p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">No gold rate recorded for this date.</p>
                  <div className="flex gap-2">
                    <input type="number" step="1" value={goldInput} placeholder="24K rate (₹/gram)"
                      onChange={e => setGoldInput(e.target.value)}
                      className="flex-1 border border-amber-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300" />
                    <button onClick={saveGold} disabled={!goldInput || goldSaving}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition flex items-center gap-1">
                      <Save size={11} /> {goldSaving ? '…' : 'Save'}
                    </button>
                  </div>
                  {goldInput && !isNaN(parseFloat(goldInput)) && (
                    <p className="text-xs text-gray-400">22K ≈ {fmt.inr(Math.round(parseFloat(goldInput) * 0.9167))} · 18K ≈ {fmt.inr(Math.round(parseFloat(goldInput) * 0.75))}</p>
                  )}
                </div>
              )}
            </div>

            {/* Dollar result */}
            <div className={`rounded-xl border p-4 ${dollar && dollar !== 'not-found' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <DollarSign size={15} className="text-green-600" />
                <span className="text-sm font-semibold text-gray-700">Dollar Rate</span>
                <span className="text-xs text-gray-400 ml-auto">{fmt.date(date)}</span>
              </div>

              {dollar && dollar !== 'not-found' ? (
                <>
                  <p className="text-2xl font-bold text-green-700">₹{parseFloat(dollar.usd_to_inr).toFixed(2)} <span className="text-sm font-normal text-green-600">per USD</span></p>
                  {dollar.source && <p className="text-xs text-gray-400 mt-1">{dollar.source}</p>}
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">No dollar rate recorded for this date.</p>
                  {isPast && (
                    <button onClick={fetchDollar} disabled={dollarFetching || dollarSaving}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition">
                      <RefreshCw size={11} className={dollarFetching ? 'animate-spin' : ''} />
                      {dollarFetching ? 'Fetching…' : 'Fetch from API'}
                    </button>
                  )}
                  <div className="flex gap-2">
                    <input type="number" step="0.01" value={dollarInput} placeholder="or enter ₹/USD"
                      onChange={e => setDollarInput(e.target.value)}
                      className="flex-1 border border-green-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-300" />
                    <button onClick={saveDollar} disabled={!dollarInput || dollarSaving || dollarFetching}
                      className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition flex items-center gap-1">
                      <Save size={11} /> {dollarSaving ? '…' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tax Rate Card ────────────────────────────────────────────────────────────
function TaxRateCard() {
  const [current, setCurrent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ gst_percentage: '', customs_duty: '', state_tax: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/rates/tax/latest`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success && data.rate) setCurrent(data.rate);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setForm({ gst_percentage: current?.gst_percentage ?? '', customs_duty: current?.customs_duty ?? '', state_tax: current?.state_tax ?? '' });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/rates/tax/manual`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          gst_percentage: form.gst_percentage ? parseFloat(form.gst_percentage) : null,
          customs_duty: form.customs_duty ? parseFloat(form.customs_duty) : null,
          state_tax: form.state_tax ? parseFloat(form.state_tax) : null,
        }),
      });
      const data = await res.json();
      if (data.success) { flash('ok', 'Tax rates saved'); setEditing(false); await load(); }
      else flash('err', data.message || 'Save failed');
    } catch { flash('err', 'Network error'); }
    finally { setSaving(false); }
  };

  return (
    <RateCard icon={Calculator} iconColor="text-purple-500" title="Tax Rates">
      {msg && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-4 ${msg.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {msg.type === 'ok' ? <CheckCircle size={14} /> : <AlertCircle size={14} />} {msg.text}
        </div>
      )}
      {!editing ? (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4">
            {[
              { label: 'GST', value: current?.gst_percentage, color: 'text-purple-700' },
              { label: 'Customs Duty', value: current?.customs_duty, color: 'text-red-600' },
              { label: 'State Tax', value: current?.state_tax, color: 'text-indigo-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{fmt.pct(value)}</p>
              </div>
            ))}
          </div>
          <button onClick={startEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition">
            <Edit2 size={12} /> Edit Tax Rates
          </button>
          {current?.updated_at && <p className="mt-3 text-xs text-gray-400">Last updated: {fmt.date(current.updated_at)}</p>}
        </>
      ) : (
        <div className="space-y-4">
          {[
            { key: 'gst_percentage', label: 'GST (%)', placeholder: 'e.g. 3' },
            { key: 'customs_duty', label: 'Customs Duty (%)', placeholder: 'e.g. 15' },
            { key: 'state_tax', label: 'State Tax (%)', placeholder: 'e.g. 0' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
              <input type="number" step="0.01" value={form[key]} placeholder={placeholder}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition">
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition">
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </RateCard>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const RateManager = () => (
  <div className="p-6 pb-12 max-w-5xl mx-auto">
    <PageIdentifier pageId={SCREEN_IDS?.RATES?.MANUAL_ENTRY || 'RATE-003'} pageName="Rate Manager" />
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-gray-800">Rate Manager</h1>
      <p className="text-sm text-gray-500 mt-0.5">Manage gold, dollar, and tax rates used across all estimates and billing</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
      <GoldRateCard />
      <DollarRateCard />
    </div>

    <div className="mb-5">
      <DateLookupPanel />
    </div>

    <TaxRateCard />
  </div>
);

export default RateManager;
